/**
 * Redo Issues List Component
 *
 * Displays list of items pending redo (reprint).
 * Extracted from IssuesPage for better code organization.
 */
import { useTranslation } from 'react-i18next';
import { RotateCcw } from 'lucide-react';
import { PageLoader } from '@/components/loading/PageLoader';
import { useProductionIssues } from '../hooks/useProductionIssues';
import { IssueCard } from './IssueCard';

interface RedoIssuesListProps {
  locationId: string;
}

export function RedoIssuesList({ locationId }: RedoIssuesListProps) {
  const { t } = useTranslation('production');
  const { data: issues, isLoading } = useProductionIssues(locationId, {
    status: 'OPEN',
    issueType: 'REDO_PENDING',
  });

  if (isLoading) {
    return <PageLoader variant="production" pool="issues" />;
  }

  if (!issues || issues.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <RotateCcw className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
        <p className="mt-4 text-lg font-medium">{t('issues.redos.noPending')}</p>
        <p className="text-sm text-muted-foreground">
          {t('issues.redos.willAppearHere')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {issues.map((issue) => (
        <IssueCard key={issue.id} issue={issue} />
      ))}
    </div>
  );
}
